import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updatePageMeta, setJsonLd, addBreadcrumbSchema } from '../utils/seo';
import { SITE, BLOG_AUTHOR } from '../data/blogConfig';
import { metaFor, canonicalFor } from '../seo/routeMeta';
import { personSchema } from '../seo/blogSchema';
import { ABOUT_SECTIONS, ABOUT_TAGLINE } from '../data/about';

const CANONICAL = canonicalFor('/about');

// Thomas will send a headshot; paste its Cloudinary URL here and it appears.
export const ABOUT_HEADSHOT_URL = '';

/**
 * /about — who runs Utah Realty Photos (2026-10-16).
 *
 * The author page every blog byline links to, and the Person that the
 * BlogPosting schema names (@id …/about#thomas). The copy lives in
 * data/about.js so the server renderer prints the same words. sameAs is left
 * off on purpose: the social profiles are the BUSINESS's (they sit on the
 * ProfessionalService block in index.html); add Thomas's own profiles here
 * if he wants them.
 *
 * Same design as /locations (dark hero, Playfair, gold accent).
 */
function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    updatePageMeta(metaFor('/about'));
    // Same Person block the server prints into the raw HTML (id structured-data).
    setJsonLd('structured-data', personSchema({ image: ABOUT_HEADSHOT_URL || undefined }));
    addBreadcrumbSchema([
      { name: 'Home', url: `${SITE}/` },
      { name: 'About', url: CANONICAL }
    ]);
  }, []);

  const p = { fontSize: '1.1rem', lineHeight: '1.8', color: '#374151', margin: '0 0 1.25rem' };
  const h2 = { fontSize: '1.5rem', margin: '2.25rem 0 0.75rem', fontFamily: "'Playfair Display', serif", borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' };

  return (
    <div style={{ paddingTop: '140px', minHeight: '60vh' }}>
      <section style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white', padding: '60px 0', textAlign: 'center' }}>
        <div className="container">
          <p style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>
            {BLOG_AUTHOR.jobTitle}
          </p>
          <h1 style={{ fontSize: '2.5rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>About Thomas</h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.85 }}>{ABOUT_TAGLINE}</p>
        </div>
      </section>

      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '820px', margin: '0 auto', padding: '0 1.5rem' }}>
          {ABOUT_HEADSHOT_URL && (
            <img src={ABOUT_HEADSHOT_URL} alt="Thomas, owner and photographer of Utah Realty Photos" width="240" height="240"
              style={{ width: 240, height: 240, objectFit: 'cover', borderRadius: '50%', display: 'block', margin: '0 auto 2rem', border: '4px solid #c9a96e' }} />
          )}

          {ABOUT_SECTIONS.map((section, si) => (
            <React.Fragment key={si}>
              {section.heading && <h2 style={h2}>{section.heading}</h2>}
              {section.paragraphs.map((pieces, pi) => (
                <p style={p} key={pi}>
                  {pieces.map((piece, k) => {
                    if (typeof piece === 'string') return <React.Fragment key={k}>{piece}</React.Fragment>;
                    if (piece.to) return <Link key={k} to={piece.to}>{piece.text}</Link>;
                    return <a key={k} href={piece.href}>{piece.text}</a>;
                  })}
                </p>
              ))}
            </React.Fragment>
          ))}

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '2rem' }}>
            <Link to="/book" style={{ padding: '14px 32px', background: '#c9a96e', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' }}>
              Book a Shoot
            </Link>
            <Link to="/portfolio" style={{ padding: '14px 32px', border: '2px solid #1a1a2e', color: '#1a1a2e', borderRadius: '6px', textDecoration: 'none', fontWeight: '500' }}>
              See the Portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
